import { shallowRef, readonly, watch, type Ref } from 'vue';

/**
 * Module-level state.
 *
 * captchaToken  – the raw token string passed to onSuccess. This is the
 *                 most reliable source: no async, no timeout risk.
 * fetchPatched  – once-guard so we only wrap window.fetch once per page load.
 *
 * WHY NOT AwsWafIntegration.fetch as window.fetch replacement:
 *   AwsWafIntegration.fetch reads window.fetch lazily (at call time).
 *   window.fetch = AwsWafIntegration.fetch creates infinite recursion → crash.
 *
 * CORRECT APPROACH:
 *   Save native fetch as originalFetch, then wrap window.fetch.
 *   In the wrapper, read the stored CAPTCHA token (or fall back to getToken())
 *   and inject it as x-aws-waf-token for cross-domain Cognito requests.
 *   Amplify's fetchTransferHandler calls the global `fetch` at call time,
 *   so this wrapper is guaranteed to intercept every Amplify → Cognito request.
 *
 * TOKEN DOMAIN:
 *   The WAF web ACL on Cognito must have the page hostname in its token domain
 *   list for WAF to accept tokens issued by the CAPTCHA SDK on this page.
 *
 * captchaToken persists at module level across component re-mounts (e.g. locale
 *   switches that force :key re-creation). New instances initialise `solved` from
 *   captchaToken !== null so waitForCaptcha() skips the overlay when a token is
 *   already available.
 */
let captchaToken: string | null = null;
let fetchPatched = false;

function patchFetch(): void {
  if (fetchPatched) return;
  fetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    let url: string;
    if (typeof input === 'string') {
      url = input;
    } else if (input instanceof URL) {
      url = input.href;
    } else {
      url = (input as Request).url;
    }

    if (url.includes('cognito-idp')) {
      // Primary: use token stored directly from onSuccess (no async, no timeout).
      // Fallback: getToken() in case the component was re-mounted after initial solve.
      let token: string | null = captchaToken;
      if (!token && window.AwsWafIntegration) {
        try {
          token = await window.AwsWafIntegration.getToken();
        } catch (e) {
          // getToken() timed out or failed — proceed without token
        }
      }

      if (token) {
        const headers = new Headers(init?.headers);
        headers.set('x-aws-waf-token', token);
        return originalFetch(input, { ...init, headers });
      }
    }

    return originalFetch(input, init);
  };
}

export default function useWafCaptcha(container: Ref<HTMLElement | null>, locale?: Ref<string>) {
  // Initialise from captchaToken so re-mounts (e.g. locale switch) don't re-show the overlay.
  const solved = shallowRef(captchaToken !== null);
  const failed = shallowRef(false);
  const loading = shallowRef(true);

  function renderCaptcha(): void {
    if (!container.value) return;
    loading.value = false;

    window.AwsWafCaptcha.renderCaptcha(container.value, {
      apiKey: process.env.VUE_APP_WAF_CAPTCHA_API_KEY as string,
      defaultLocale: locale?.value,
      onSuccess: (wafToken: string) => {
        captchaToken = wafToken;
        patchFetch();
        solved.value = true;
      },
      onError: (err: Error) => {
        failed.value = true;
      },
    });
  }

  let pollInterval: ReturnType<typeof setInterval> | null = null;

  watch(container, (el) => {
    if (el) {
      // Container just became available in the DOM — render the CAPTCHA widget.
      if (window.AwsWafCaptcha) {
        renderCaptcha();
        return;
      }
      // jsapi.js is loaded with defer; poll until AwsWafCaptcha is available.
      pollInterval = setInterval(() => {
        if (window.AwsWafCaptcha) {
          clearInterval(pollInterval!);
          pollInterval = null;
          renderCaptcha();
        }
      }, 100);
    } else {
      // Container removed from DOM — clean up poll interval and reset loading state.
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      loading.value = true;
    }
  });

  return {
    solved: readonly(solved),
    failed: readonly(failed),
    loading: readonly(loading),
  };
}
